/**
 * PUBLIC WISHLIST PAGE
 * 
 * Dedicated page for viewing a user's wishlist with:
 * - Search by name/brand
 * - Filter by ISO priority (ISO/DISO/Unicorn)
 * - Filter by item type
 * - Sort options
 * - "I Have This!" contact button (when allowed)
 * 
 * Route: /wishlist/:username
 * 
 * UPDATED: Glassmorphic styling to match DestashPage
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import type { CollectionItem } from '../types';
import WishlistContactModal from '../components/WishlistContactModal';
import { PriorityColors, type PriorityKey } from '../constants/categoryColors';
import PolishDetailModal from '../components/polish/PolishDetailModal';
import PlateDetailModal from '../components/plates/PlateDetailModal';
import DecalDetailModal from '../components/decals/DecalDetailModal';
import EmbellishmentDetailModal from '../components/embellishments/EmbellishmentDetailModal';
import ToolDetailModal from '../components/tools/ToolDetailModal';
import BuffingPowderDetailModal from '../components/powders/BuffingPowderDetailModal';
import AcrylicDetailModal from '../components/acrylics/AcrylicDetailModal';
import PageVisibilityBanner from '../components/PageVisibilityBanner';
import { Search, Filter, ArrowLeft, Gift, Loader2, X, CheckSquare, Square, ExternalLink } from 'lucide-react';

interface UserProfile {
  username: string;
  displayName: string;
  avatarUrl?: string;
  privacy?: {
    showWishlist?: boolean;
    allowWishlistContact?: boolean;
  };
}

type SortOption = 'priority' | 'unicorn-first' | 'diso-first' | 'iso-first' | 'brand' | 'name' | 'recent' | 'item-type';
type PriorityFilter = 'all' | 'iso' | 'diso' | 'unicorn';
type ItemTypeFilter = 'all' | 'polish' | 'plate' | 'tool' | 'embellishment' | 'transfer' | 'powder' | 'acrylic';

export default function PublicWishlist() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  
  // Data state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [items, setItems] = useState<CollectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter/search state
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [itemTypeFilter, setItemTypeFilter] = useState<ItemTypeFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('priority');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Bulk selection state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  
  // Modal state
  const [selectedItem, setSelectedItem] = useState<CollectionItem | null>(null);
  const [wishlistContactItem, setWishlistContactItem] = useState<CollectionItem | null>(null);
  
  // Current user state
  const [isOwnWishlist, setIsOwnWishlist] = useState(false);
  const currentUser = getCurrentUser();

  // Load wishlist data
  useEffect(() => {
    const loadWishlist = async () => {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }

      try {
        // Look up user ID from username
        const usernameDoc = await getDoc(doc(db, 'usernames', username.toLowerCase()));
        
        if (!usernameDoc.exists()) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const foundUserId = usernameDoc.data()?.userId || usernameDoc.data()?.uid;
        if (!foundUserId) {
          setError('User not found');
          setLoading(false);
          return;
        }
        
        setUserId(foundUserId);
        setIsOwnWishlist(currentUser?.uid === foundUserId);

        // Load user profile
        const userDoc = await getDoc(doc(db, 'users', foundUserId));
        if (!userDoc.exists()) {
          setError('Profile not found');
          setLoading(false);
          return;
        }

        const userData = userDoc.data();
        const userProfile: UserProfile = {
          username: userData.username || username,
          displayName: userData.displayName || userData.username || 'Unknown',
          avatarUrl: userData.avatarUrl,
          privacy: userData.privacy
        };
        setProfile(userProfile);

        // Check privacy - allow if own wishlist or wishlist is public
        const isOwn = currentUser?.uid === foundUserId;
        if (!isOwn && !userData.privacy?.showWishlist) {
          setError('This wishlist is private');
          setLoading(false);
          return;
        }

        // Load items
        const itemsRef = collection(db, 'users', foundUserId, 'items');
        const itemsSnap = await getDocs(itemsRef);
        
        const loadedItems: CollectionItem[] = [];
        itemsSnap.forEach((doc) => {
          const data = doc.data();
          // Only include wishlist items that aren't destashed. Also hide
          // items marked private from viewers other than the owner — the
          // owner keeps seeing their own private wishlist entries; the
          // lock badge is applied by the tile.
          if (
            data.onWishlist &&
            !data.isDestashed &&
            (isOwn || data.isPrivate !== true)
          ) {
            loadedItems.push({
              id: doc.id,
              ...data,
              dateAdded: data.dateAdded?.toDate() || new Date(),
            } as CollectionItem);
          }
        });

        setItems(loadedItems);
      } catch (err) {
        console.error('Error loading wishlist:', err);
        setError('Failed to load wishlist');
      } finally {
        setLoading(false);
      }
    };

    loadWishlist();
  }, [username, currentUser?.uid]);

  // Get item image helper
  const getItemImage = (item: CollectionItem): string | null => {
    if (item.photos && item.photos.length > 0) {
      const defaultPhoto = item.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : item.photos[0].url;
    }
    if (item.photoUrl) return item.photoUrl;
    return null;
  };

  // Get item type label
  const getItemTypeLabel = (type: string): string => {
    switch (type) {
      case 'polish': return '💅 Polish';
      case 'plate': return '🔲 Plate';
      case 'tool': return '🛠️ Tool';
      case 'embellishment': return '💎 Embellishment';
      case 'transfer': return '🦋 Decal';
      case 'powder': return '✨ Powder';
      case 'acrylic': return '💪 Acrylic';
      default: return type;
    }
  };

  // Get priority sort value
  const getPrioritySortValue = (priority?: string): number => {
    switch (priority) {
      case 'unicorn': return 0;
      case 'diso': return 1;
      case 'iso': return 2;
      default: return 3;
    }
  };

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let result = [...items];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.brand.toLowerCase().includes(query)
      );
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      result = result.filter(item => (item.isoPriority || 'iso') === priorityFilter);
    }

    // Item type filter
    if (itemTypeFilter !== 'all') {
      result = result.filter(item => item.itemType === itemTypeFilter);
    }

    // Sort
    switch (sortBy) {
      case 'priority':
        result.sort((a, b) => getPrioritySortValue(a.isoPriority) - getPrioritySortValue(b.isoPriority));
        break;
      case 'unicorn-first':
        result.sort((a, b) => {
          if (a.isoPriority === 'unicorn' && b.isoPriority !== 'unicorn') return -1;
          if (a.isoPriority !== 'unicorn' && b.isoPriority === 'unicorn') return 1;
          return a.name.localeCompare(b.name);
        });
        break;
      case 'diso-first':
        result.sort((a, b) => {
          if (a.isoPriority === 'diso' && b.isoPriority !== 'diso') return -1;
          if (a.isoPriority !== 'diso' && b.isoPriority === 'diso') return 1;
          return a.name.localeCompare(b.name);
        });
        break;
      case 'iso-first':
        result.sort((a, b) => {
          const aIsIso = !a.isoPriority || a.isoPriority === 'iso';
          const bIsIso = !b.isoPriority || b.isoPriority === 'iso';
          if (aIsIso && !bIsIso) return -1;
          if (!aIsIso && bIsIso) return 1;
          return a.name.localeCompare(b.name);
        });
        break;
      case 'brand':
        result.sort((a, b) => a.brand.localeCompare(b.brand));
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'item-type':
        result.sort((a, b) => a.itemType.localeCompare(b.itemType));
        break;
      case 'recent':
        result.sort((a, b) => {
          const dateA = a.dateAdded instanceof Date ? a.dateAdded : new Date(a.dateAdded || 0);
          const dateB = b.dateAdded instanceof Date ? b.dateAdded : new Date(b.dateAdded || 0);
          return dateB.getTime() - dateA.getTime();
        });
        break;
    }

    return result;
  }, [items, searchQuery, priorityFilter, itemTypeFilter, sortBy]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, priorityFilter, itemTypeFilter, sortBy, itemsPerPage]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push('...');
      }
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // Get unique brands for stats
  const uniqueBrands = useMemo(() => {
    return [...new Set(items.map(i => i.brand))];
  }, [items]);

  // Get priority counts
  const priorityCounts = useMemo(() => {
    return {
      unicorn: items.filter(i => i.isoPriority === 'unicorn').length,
      diso: items.filter(i => i.isoPriority === 'diso').length,
      iso: items.filter(i => !i.isoPriority || i.isoPriority === 'iso').length,
    };
  }, [items]);

  // Can contact check
  const canContact = !isOwnWishlist && currentUser && profile?.privacy?.allowWishlistContact;

  // Get priority badge - using centralized PriorityColors
  const getPriorityBadge = (priority?: string) => {
    const key = (priority || 'iso') as PriorityKey;
    const colors = PriorityColors[key] || PriorityColors.iso;
    return { bg: colors.bg, text: colors.label };
  };

  // Bulk update handler
  const handleBulkPriorityUpdate = async (priority: 'iso' | 'diso' | 'unicorn') => {
    if (!userId || selectedIds.size === 0) return;
    
    setIsBulkUpdating(true);
    
    try {
      const updatePromises = Array.from(selectedIds).map(itemId => {
        const itemRef = doc(db, 'users', userId, 'items', itemId);
        return updateDoc(itemRef, { isoPriority: priority });
      });
      
      await Promise.all(updatePromises);
      
      setItems(prev => prev.map(item => 
        selectedIds.has(item.id) 
          ? { ...item, isoPriority: priority }
          : item
      ));
      
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error bulk updating priorities:', err);
      alert('Failed to update some items. Please try again.');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
    setSelectedIds(newSet);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-glass" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Gift className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-glass mb-2 font-heading">
            {error}
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="text-glass hover:text-glass-secondary"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Visibility Banner - Only for profile owner */}
        {isOwnWishlist && profile?.username && (
          <PageVisibilityBanner 
            isPublic={profile?.privacy?.showWishlist !== false}
            publicPath={`/wishlist/${profile.username}`}
            pageType="wishlist"
            className="mb-6"
            isPreviewMode={true}
            onPreview={() => navigate('/my-wants')}
          />
        )}

        {/* Header - GLASSMORPHIC */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/profile/${username}`)}
            className="p-2 hover:bg-slate-800/60 rounded-lg transition-default"
          >
            <ArrowLeft className="icon-base text-glass-secondary" />
          </button>
          
          <div className="flex items-center gap-4 flex-1">
            {profile?.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt={profile.displayName}
                className="w-14 h-14 rounded-full object-cover border-2 border-slate-600"
              />
            ) : (
              <div className="w-14 h-14 rounded-full rainbow-gradient flex items-center justify-center text-white text-xl font-bold">
                {profile?.displayName?.charAt(0) || '?'}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-glass font-heading">
                {isOwnWishlist ? 'My Wishlist' : `${profile?.displayName}'s Wishlist`}
              </h1>
              <p className="text-glass-secondary font-body">
                {items.length} items from {uniqueBrands.length} brands
              </p>
            </div>
          </div>

          {/* View Profile Link */}
          <Link
            to={`/profile/${username}`}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 hover:bg-white/70 rounded-lg text-glass-secondary transition-default font-body"
          >
            <ExternalLink className="icon-sm" />
            View Profile
          </Link>
        </div>

        {/* Stats Bar - GLASSMORPHIC */}
        <div className="glass-card p-4 mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-xl">
              <p className="text-2xl font-bold text-purple-600">{priorityCounts.unicorn}</p>
              <p className="text-sm text-purple-500">🦄 Unicorn</p>
            </div>
            <div className="p-3 bg-rose-500/20 rounded-xl">
              <p className="text-2xl font-bold text-rose-600">{priorityCounts.diso}</p>
              <p className="text-sm text-rose-500">🔥 DISO</p>
            </div>
            <div className="p-3 bg-pink-500/20 rounded-xl">
              <p className="text-2xl font-bold text-pink-600">{priorityCounts.iso}</p>
              <p className="text-sm text-pink-500">💎 ISO</p>
            </div>
          </div>
        </div>

        {/* Bulk Edit Toggle - Only for own wishlist */}
        {isOwnWishlist && (
          <div className="flex justify-end mb-4">
            <button
              onClick={() => {
                setIsSelectionMode(!isSelectionMode);
                setSelectedIds(new Set());
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-default flex items-center gap-2 ${
                isSelectionMode
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
              }`}
            >
              <CheckSquare className="icon-sm" />
              {isSelectionMode ? 'Cancel' : 'Bulk Edit'}
            </button>
          </div>
        )}

        {/* Bulk action bar */}
        {isSelectionMode && selectedIds.size > 0 && (
          <div className="backdrop-blur-md bg-slate-700/20 rounded-2xl border border-slate-500 p-4 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-glass font-body">
                {selectedIds.size} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkPriorityUpdate('iso')}
                  disabled={isBulkUpdating}
                  className={`px-3 py-1.5 ${PriorityColors.iso.bg} ${PriorityColors.iso.bgHover} text-white text-sm font-bold rounded-lg transition-default disabled:opacity-50`}
                >
                  {PriorityColors.iso.label.replace('💎 ', '💎 Set ')}
                </button>
                <button
                  onClick={() => handleBulkPriorityUpdate('diso')}
                  disabled={isBulkUpdating}
                  className={`px-3 py-1.5 ${PriorityColors.diso.bg} ${PriorityColors.diso.bgHover} text-white text-sm font-bold rounded-lg transition-default disabled:opacity-50`}
                >
                  {PriorityColors.diso.label.replace('🔥 ', '🔥 Set ')}
                </button>
                <button
                  onClick={() => handleBulkPriorityUpdate('unicorn')}
                  disabled={isBulkUpdating}
                  className={`px-3 py-1.5 ${PriorityColors.unicorn.bg} ${PriorityColors.unicorn.bgHover} text-white text-sm font-bold rounded-lg transition-default disabled:opacity-50`}
                >
                  {PriorityColors.unicorn.label.replace('🦄 ', '🦄 Set ')}
                </button>
              </div>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="ml-auto text-sm text-glass-secondary hover:text-glass"
              >
                Clear selection
              </button>
            </div>
          </div>
        )}

        {/* Select all buttons */}
        {isSelectionMode && (
          <div className="mb-4 flex items-center gap-2">
            <button
              onClick={() => {
                const currentPageIds = paginatedItems.map(i => i.id);
                const allSelected = currentPageIds.every(id => selectedIds.has(id));
                if (allSelected) {
                  const newSet = new Set(selectedIds);
                  currentPageIds.forEach(id => newSet.delete(id));
                  setSelectedIds(newSet);
                } else {
                  const newSet = new Set(selectedIds);
                  currentPageIds.forEach(id => newSet.add(id));
                  setSelectedIds(newSet);
                }
              }}
              className="text-sm text-glass-secondary hover:text-glass font-medium"
            >
              {paginatedItems.every(i => selectedIds.has(i.id)) 
                ? 'Deselect all on this page' 
                : 'Select all on this page'}
            </button>
            <span className="text-gray-400">|</span>
            <button
              onClick={() => {
                const allFilteredIds = filteredItems.map(i => i.id);
                const allSelected = allFilteredIds.every(id => selectedIds.has(id));
                if (allSelected) {
                  setSelectedIds(new Set());
                } else {
                  setSelectedIds(new Set(allFilteredIds));
                }
              }}
              className="text-sm text-glass-secondary hover:text-glass font-medium"
            >
              {filteredItems.every(i => selectedIds.has(i.id)) 
                ? `Deselect all ${filteredItems.length} items` 
                : `Select all ${filteredItems.length} filtered items`}
            </button>
          </div>
        )}

        {/* Search & Filter Bar - GLASSMORPHIC */}
        <div className="glass-card p-4 mb-6">
          {/* Search */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
              <input
                type="text"
                placeholder="Search by name or brand..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800/60 border-0 rounded-xl focus:ring-2 focus:ring-slate-500 text-glass placeholder:text-glass-muted font-body"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-800/40 rounded-full"
                >
                  <X className="icon-sm text-glass-muted" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-xl transition-default flex items-center gap-2 ${
                showFilters || priorityFilter !== 'all' || itemTypeFilter !== 'all'
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
              }`}
            >
              <Filter className="icon-sm" />
              Filters
            </button>
          </div>

          {/* Quick Priority Filters */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setPriorityFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-default ${
                priorityFilter === 'all'
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
              }`}
            >
              All ({items.length})
            </button>
            <button
              onClick={() => setPriorityFilter('unicorn')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-default ${
                priorityFilter === 'unicorn'
                  ? `${PriorityColors.unicorn.bg} text-white`
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
              }`}
            >
              {PriorityColors.unicorn.label} ({priorityCounts.unicorn})
            </button>
            <button
              onClick={() => setPriorityFilter('diso')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-default ${
                priorityFilter === 'diso'
                  ? `${PriorityColors.diso.bg} text-white`
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
              }`}
            >
              {PriorityColors.diso.label} ({priorityCounts.diso})
            </button>
            <button
              onClick={() => setPriorityFilter('iso')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-default ${
                priorityFilter === 'iso'
                  ? `${PriorityColors.iso.bg} text-white`
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
              }`}
            >
              {PriorityColors.iso.label} ({priorityCounts.iso})
            </button>
          </div>

          {/* Expanded Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-600 space-y-4">
              {/* Item type filter */}
              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-2">Item Type</label>
                <div className="flex flex-wrap gap-2">
                  {(['all', 'polish', 'plate', 'tool', 'embellishment', 'transfer', 'powder', 'acrylic'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setItemTypeFilter(type)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-default ${
                        itemTypeFilter === type
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                      }`}
                    >
                      {type === 'all' ? 'All Types' : getItemTypeLabel(type)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="px-4 py-2 bg-slate-800/60 rounded-xl border-0 focus:ring-2 focus:ring-slate-500 font-body"
                >
                  <option value="priority">Priority (Unicorn → DISO → ISO)</option>
                  <option value="unicorn-first">🦄 Unicorns First</option>
                  <option value="diso-first">🔥 DISO First</option>
                  <option value="iso-first">💎 ISO First</option>
                  <option value="brand">Brand A-Z</option>
                  <option value="name">Name A-Z</option>
                  <option value="item-type">Item Type</option>
                  <option value="recent">Recently Added</option>
                </select>
              </div>

              {/* Clear filters */}
              {(priorityFilter !== 'all' || itemTypeFilter !== 'all' || searchQuery) && (
                <button
                  onClick={() => {
                    setPriorityFilter('all');
                    setItemTypeFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-sm text-glass-secondary hover:text-glass font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Results count and per-page */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <p className="text-sm text-glass-secondary font-body">
            Showing {filteredItems.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredItems.length)} of {filteredItems.length} items
            {filteredItems.length !== items.length && ` (${items.length} total)`}
          </p>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-glass-muted">Show:</span>
            {[25, 50, 100].map((count) => (
              <button
                key={count}
                onClick={() => setItemsPerPage(count)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-default ${
                  itemsPerPage === count
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                }`}
              >
                {count}
              </button>
            ))}
          </div>
        </div>

        {/* Empty state */}
        {filteredItems.length === 0 && (
          <div className="glass-card p-12 text-center">
            <Gift className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-glass mb-2 font-body">
              No items found
            </h2>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-glass-secondary hover:text-glass font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {/* Items Grid */}
        {filteredItems.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {paginatedItems.map((item) => {
              const priorityBadge = getPriorityBadge(item.isoPriority);
              const isSelected = selectedIds.has(item.id);
              
              return (
                <div 
                  key={item.id} 
                  className={`bg-white rounded-xl shadow-md overflow-hidden group relative hover:shadow-lg transition-shadow ${
                    isSelectionMode && isSelected ? 'ring-2 ring-slate-500' : ''
                  }`}
                >
                  {/* Selection checkbox overlay */}
                  {isSelectionMode && (
                    <button
                      onClick={() => toggleItemSelection(item.id)}
                      className="absolute top-2 left-2 z-10 w-6 h-6 rounded bg-white shadow-md flex items-center justify-center transition-default"
                    >
                      {isSelected ? (
                        <CheckSquare className="icon-md text-slate-700" />
                      ) : (
                        <Square className="icon-md text-gray-400" />
                      )}
                    </button>
                  )}
                  
                  {/* Image */}
                  <div 
                    onClick={() => isSelectionMode ? toggleItemSelection(item.id) : setSelectedItem(item)}
                    className="aspect-square relative cursor-pointer overflow-hidden"
                  >
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center text-4xl">
                        💝
                      </div>
                    )}
                    
                    {/* Priority badge */}
                    <span className={`absolute top-2 ${isSelectionMode ? 'left-10' : 'left-2'} px-2 py-1 rounded-full text-xs font-bold text-white ${priorityBadge.bg} transition-all`}>
                      {priorityBadge.text}
                    </span>
                    
                    {/* Item type badge */}
                    <span className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold bg-white/90 text-gray-600">
                      {getItemTypeLabel(item.itemType).split(' ')[0]}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="text-xs text-gray-500 font-bold truncate font-heading">
                      {item.brand}
                    </p>
                    <p className="font-bold text-gray-900 truncate font-body">
                      {item.name}
                    </p>
                    
                    {/* Contact button */}
                    {canContact && !isSelectionMode && (
                      <button
                        onClick={() => setWishlistContactItem(item)}
                        className="mt-2 w-full flex items-center justify-center gap-1 px-3 py-1.5 btn-primary text-xs font-bold rounded-lg transition-default font-body"
                      >
                        <Gift className="w-3.5 h-3.5" />
                        I Have This!
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-xl font-medium transition-default ${
                currentPage === 1
                  ? 'bg-white/30 text-glass-muted cursor-not-allowed'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
              }`}
            >
              ← Prev
            </button>

            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="px-2 text-glass-muted">...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page as number)}
                  className={`w-10 h-10 rounded-xl font-medium transition-default ${
                    currentPage === page
                      ? 'bg-slate-700 text-white'
                      : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                  }`}
                >
                  {page}
                </button>
              )
            ))}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-xl font-medium transition-default ${
                currentPage === totalPages
                  ? 'bg-white/30 text-glass-muted cursor-not-allowed'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
              }`}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Detail Modals */}
      {selectedItem && selectedItem.itemType === 'polish' && (
        <PolishDetailModal
          polish={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={async (id, updates) => {
            const updatedItem = { ...selectedItem, ...updates };
            setSelectedItem(updatedItem);
            setItems(prev => prev.map(item => 
              item.id === id ? updatedItem : item
            ));
            if (isOwnWishlist && userId) {
              try {
                const itemRef = doc(db, 'users', userId, 'items', id);
                await updateDoc(itemRef, updates);
              } catch (err) {
                console.error('Error saving update:', err);
              }
            }
          }}
          onDelete={(deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          }}
          isPublicView={!isOwnWishlist}
        />
      )}

      {selectedItem && selectedItem.itemType === 'plate' && (
        <PlateDetailModal
          plate={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={async (id, updates) => {
            const updatedItem = { ...selectedItem, ...updates };
            setSelectedItem(updatedItem);
            setItems(prev => prev.map(item => 
              item.id === id ? updatedItem : item
            ));
            if (isOwnWishlist && userId) {
              try {
                const itemRef = doc(db, 'users', userId, 'items', id);
                await updateDoc(itemRef, updates);
              } catch (err) {
                console.error('Error saving update:', err);
              }
            }
          }}
          onDelete={(deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          }}
          isPublicView={!isOwnWishlist}
        />
      )}

      {selectedItem && selectedItem.itemType === 'transfer' && (
        <DecalDetailModal
          decal={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={async (id, updates) => {
            const updatedItem = { ...selectedItem, ...updates };
            setSelectedItem(updatedItem);
            setItems(prev => prev.map(item => 
              item.id === id ? updatedItem : item
            ));
            if (isOwnWishlist && userId) {
              try {
                const itemRef = doc(db, 'users', userId, 'items', id);
                await updateDoc(itemRef, updates);
              } catch (err) {
                console.error('Error saving update:', err);
              }
            }
          }}
          onDelete={(deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          }}
          isPublicView={!isOwnWishlist}
        />
      )}

      {selectedItem && selectedItem.itemType === 'embellishment' && (
        <EmbellishmentDetailModal
          embellishment={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={async (id, updates) => {
            const updatedItem = { ...selectedItem, ...updates };
            setSelectedItem(updatedItem);
            setItems(prev => prev.map(item => 
              item.id === id ? updatedItem : item
            ));
            if (isOwnWishlist && userId) {
              try {
                const itemRef = doc(db, 'users', userId, 'items', id);
                await updateDoc(itemRef, updates);
              } catch (err) {
                console.error('Error saving update:', err);
              }
            }
          }}
          onDelete={(deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          }}
          isPublicView={!isOwnWishlist}
        />
      )}

      {selectedItem && selectedItem.itemType === 'tool' && (
        <ToolDetailModal
          tool={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={async (id, updates) => {
            const updatedItem = { ...selectedItem, ...updates };
            setSelectedItem(updatedItem);
            setItems(prev => prev.map(item => 
              item.id === id ? updatedItem : item
            ));
            if (isOwnWishlist && userId) {
              try {
                const itemRef = doc(db, 'users', userId, 'items', id);
                await updateDoc(itemRef, updates);
              } catch (err) {
                console.error('Error saving update:', err);
              }
            }
          }}
          onDelete={(deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          }}
          isPublicView={!isOwnWishlist}
        />
      )}

      {selectedItem && selectedItem.itemType === 'powder' && (
        <BuffingPowderDetailModal
          powder={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={async (id, updates) => {
            const updatedItem = { ...selectedItem, ...updates };
            setSelectedItem(updatedItem);
            setItems(prev => prev.map(item => 
              item.id === id ? updatedItem : item
            ));
            if (isOwnWishlist && userId) {
              try {
                const itemRef = doc(db, 'users', userId, 'items', id);
                await updateDoc(itemRef, updates);
              } catch (err) {
                console.error('Error saving update:', err);
              }
            }
          }}
          onDelete={(deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          }}
          isPublicView={!isOwnWishlist}
        />
      )}

      {selectedItem && selectedItem.itemType === 'acrylic' && (
        <AcrylicDetailModal
          acrylic={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={async (id, updates) => {
            const updatedItem = { ...selectedItem, ...updates };
            setSelectedItem(updatedItem);
            setItems(prev => prev.map(item => 
              item.id === id ? updatedItem : item
            ));
            if (isOwnWishlist && userId) {
              try {
                const itemRef = doc(db, 'users', userId, 'items', id);
                await updateDoc(itemRef, updates);
              } catch (err) {
                console.error('Error saving update:', err);
              }
            }
          }}
          onDelete={(deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          }}
          isPublicView={!isOwnWishlist}
        />
      )}

      {/* PublicWishlist Contact Modal */}
      {wishlistContactItem && userId && profile && (
        <WishlistContactModal
          isOpen={!!wishlistContactItem}
          onClose={() => setWishlistContactItem(null)}
          currentUserId={currentUser?.uid || ''}
          wisherId={userId}
          wisherUsername={profile.username}
          wisherDisplayName={profile.displayName}
          item={{
            id: wishlistContactItem.id,
            name: wishlistContactItem.name,
            brand: wishlistContactItem.brand,
            isoPriority: wishlistContactItem.isoPriority,
            photos: wishlistContactItem.photos,
            photoUrl: wishlistContactItem.photoUrl
          }}
        />
      )}
    </div>
  );
}
